import type { ClientTheme, BrandPalette } from '@type/theme';
import {
  QikServeLogo,
  StarbucksLogo,
  YoSushiLogo,
} from '@components/common/Logo';
import type { BrandConfig } from '@/types/store';
import type { OrgConfig } from '@/types/orgConfig';

const qikservePalette: { light: BrandPalette; dark: BrandPalette } = {
  light: {
    buttonPrimaryBg: '#8A2BE2',
    buttonPrimaryBgHover: '#7B1FA2',
    buttonPrimaryText: '#ffffff',
    buttonPrimaryBorder: '#8A2BE2',
    buttonSecondaryText: '#8A2BE2',
    buttonSecondaryBorder: '#8A2BE2',
    interactivePrimary: '#8A2BE2',
    interactivePrimaryHover: '#7B1FA2',
    borderFocus: '#8A2BE2',
    accent: '#7B1FA2',
    onPrimary: '#FFFFFF',
    success: '#16A34A',
    warning: '#F59E0B',
    error: '#DC2626',
    info: '#0284C7',
  },
  dark: {
    buttonPrimaryBg: '#8A2BE2',
    buttonPrimaryBgHover: '#9D4BE7',
    buttonPrimaryText: '#ffffff',
    buttonPrimaryBorder: '#8A2BE2',
    buttonSecondaryText: '#C9A9F3',
    buttonSecondaryBorder: '#334155',
    interactivePrimary: '#A364EC',
    interactivePrimaryHover: '#B583F0',
    borderFocus: '#A364EC',
    accent: '#B583F0',
    onPrimary: '#FFFFFF',
    success: '#22C55E',
    warning: '#FBBF24',
    error: '#EF4444',
    info: '#38BDF8',
  },
};

const starbucksPalette: { light: BrandPalette; dark: BrandPalette } = {
  light: {
    buttonPrimaryBg: '#00704A',
    buttonPrimaryBgHover: '#005A3C',
    buttonPrimaryText: '#ffffff',
    buttonPrimaryBorder: '#00704A',
    buttonSecondaryText: '#00704A',
    buttonSecondaryBorder: '#00704A',
    interactivePrimary: '#00704A',
    interactivePrimaryHover: '#005A3C',
    borderFocus: '#00704A',
    accent: '#005A3C',
    onPrimary: '#FFFFFF',
    success: '#16A34A',
    warning: '#F59E0B',
    error: '#DC2626',
    info: '#0284C7',
  },
  dark: {
    buttonPrimaryBg: '#00704A',
    buttonPrimaryBgHover: '#1FA37D',
    buttonPrimaryText: '#ffffff',
    buttonPrimaryBorder: '#00704A',
    buttonSecondaryText: '#6EE7B7',
    buttonSecondaryBorder: '#334155',
    interactivePrimary: '#1FA37D',
    interactivePrimaryHover: '#2BC094',
    borderFocus: '#1FA37D',
    accent: '#2BC094',
    onPrimary: '#FFFFFF',
    success: '#22C55E',
    warning: '#FBBF24',
    error: '#EF4444',
    info: '#38BDF8',
  },
};

const yosushiPalette: { light: BrandPalette; dark: BrandPalette } = {
  light: {
    buttonPrimaryBg: '#FF6900',
    buttonPrimaryBgHover: '#E55A00',
    buttonPrimaryText: '#ffffff',
    buttonPrimaryBorder: '#FF6900',
    buttonSecondaryText: '#FF6900',
    buttonSecondaryBorder: '#FF6900',
    interactivePrimary: '#FF6900',
    interactivePrimaryHover: '#E55A00',
    borderFocus: '#FF6900',
    accent: '#E55A00',
    onPrimary: '#FFFFFF',
    success: '#16A34A',
    warning: '#F59E0B',
    error: '#DC2626',
    info: '#0284C7',
  },
  dark: {
    buttonPrimaryBg: '#FF6900',
    buttonPrimaryBgHover: '#FF7A1A',
    buttonPrimaryText: '#ffffff',
    buttonPrimaryBorder: '#FF6900',
    buttonSecondaryText: '#FFB37A',
    buttonSecondaryBorder: '#334155',
    interactivePrimary: '#FF7A1A',
    interactivePrimaryHover: '#FF933F',
    borderFocus: '#FF7A1A',
    accent: '#FF933F',
    onPrimary: '#FFFFFF',
    success: '#22C55E',
    warning: '#FBBF24',
    error: '#EF4444',
    info: '#38BDF8',
  },
};

export const operatorThemes: ClientTheme[] = [
  {
    id: 'qikserve',
    name: 'QikServe',
    description: 'Default purple theme for QikServe',
    palette: qikservePalette,
    Logo: QikServeLogo,
  },
  {
    id: 'starbucks',
    name: 'Starbucks',
    description: 'Starbucks green theme',
    website: 'https://www.starbucks.com',
    palette: starbucksPalette,
    Logo: StarbucksLogo,
  },
  {
    id: 'yosushi',
    name: 'Yo!Sushi',
    description: 'Yo!Sushi orange theme',
    palette: yosushiPalette,
    Logo: YoSushiLogo,
  },
];

export const getOperatorTheme = (id: string): ClientTheme | null =>
  operatorThemes.find(t => t.id === id) ?? null;

export const getAllOperatorThemes = (): ClientTheme[] => operatorThemes;

export const getThemeByBrand = (brand: string): ClientTheme | null => {
  switch ((brand || '').toLowerCase()) {
    case 'starbucks':
      return getOperatorTheme('starbucks');
    case 'yo_sushi':
    case 'yosushi':
      return getOperatorTheme('yosushi');
    case 'qikserve':
    default:
      return getOperatorTheme('qikserve');
  }
};

export const generateDynamicTheme = (brandConfig: BrandConfig): ClientTheme => {
  const primary = brandConfig.primaryColor || '#8A2BE2';
  const secondary = brandConfig.secondaryColor || primary;
  const accent = brandConfig.accentColor || secondary;
  const onPrimary = brandConfig.onPrimaryColor || '#FFFFFF';
  const success = brandConfig.successColor || '#16A34A';
  const warning = brandConfig.warningColor || '#F59E0B';
  const errorColor = brandConfig.errorColor || '#DC2626';
  const info = brandConfig.infoColor || '#0284C7';

  const palette: { light: BrandPalette; dark: BrandPalette } = {
    light: {
      buttonPrimaryBg: primary,
      buttonPrimaryBgHover: secondary,
      buttonPrimaryText: onPrimary,
      buttonPrimaryBorder: primary,
      buttonSecondaryText: primary,
      buttonSecondaryBorder: primary,
      interactivePrimary: primary,
      interactivePrimaryHover: secondary,
      borderFocus: primary,
      accent,
      onPrimary,
      success,
      warning,
      error: errorColor,
      info,
    },
    dark: {
      buttonPrimaryBg: primary,
      buttonPrimaryBgHover: secondary,
      buttonPrimaryText: onPrimary,
      buttonPrimaryBorder: primary,
      buttonSecondaryText: primary,
      buttonSecondaryBorder: '#334155',
      interactivePrimary: primary,
      interactivePrimaryHover: secondary,
      borderFocus: primary,
      accent,
      onPrimary,
      success,
      warning,
      error: errorColor,
      info,
    },
  };

  return {
    id: `dynamic-${brandConfig.name}`,
    name: brandConfig.name,
    description: `Dynamic theme for ${brandConfig.name}`,
    palette,
    Logo: QikServeLogo,
  };
};

/**
 * Builds the client theme from a CMS org-config branding payload. Shared by useBrandTheme and the
 * theme cache so the palette regenerated from cached inputs is byte-identical to the live one.
 */
export const orgBrandingToClientTheme = (branding: OrgConfig): ClientTheme =>
  generateDynamicTheme({
    name: 'org',
    logoUrl: branding.logoUrl ?? undefined,
    faviconUrl: branding.faviconUrl ?? undefined,
    primaryColor: branding.colors.primary,
    secondaryColor: branding.colors.secondary,
    accentColor: branding.colors.accent,
    onPrimaryColor: branding.colors.onPrimary,
    successColor: branding.colors.success,
    warningColor: branding.colors.warning,
    errorColor: branding.colors.error,
    infoColor: branding.colors.info,
  });
